import { Link } from "react-router-dom";
import { ArrowLeft } from "@phosphor-icons/react";

export default function Privacy() {
  return (
    <div className="min-h-screen bg-bone-100" data-testid="privacy-page">
      <header
        className="bg-bone-100/75 backdrop-blur-xl border-b border-bone-300 sticky top-0 z-10"
        style={{ paddingTop: "env(safe-area-inset-top)" }}
      >
        <div className="max-w-3xl mx-auto px-6 sm:px-8 h-16 flex items-center justify-between">
          <Link to="/" className="font-serif text-xl flex items-center gap-2">
            <span className="w-2 h-2 rounded-full bg-brand inline-block"></span> Hush
          </Link>
          <Link to="/" className="text-sm text-ink-500 hover:text-ink-900 flex items-center gap-2" data-testid="privacy-back">
            <ArrowLeft size={16} /> Back home
          </Link>
        </div>
      </header>
      <main className="max-w-3xl mx-auto px-6 sm:px-8 py-12 prose-content">
        <p className="eyebrow">Last updated: May 12, 2026</p>
        <h1 className="font-serif text-4xl sm:text-5xl leading-tight mt-3 mb-10">Privacy Policy</h1>

        <Section title="The short version">
          Hush is a private voice journal. Your recordings are transcribed by AI, analyzed for mood,
          and stored only so we can show them back to you. We don't sell your data, we don't show ads,
          and you can delete your account — and everything in it — from inside the app at any time.
        </Section>

        <Section title="What we collect">
          <List items={[
            "Account info: email, display name, and (for Google/Apple sign-in) your provider profile picture and a stable provider user ID.",
            "Journal content: audio recordings are processed in real time by OpenAI Whisper. The audio file itself is NOT stored on our servers — only the transcript text, an AI-detected mood label (e.g. \"calm\", \"anxious\"), a sentiment score, and short emotion tags.",
            "Usage data: timestamps of when you record entries, your streak history, and your reminder preferences.",
            "Notification tokens: if you enable push notifications, the browser/native push subscription identifier so we can deliver your daily reminder.",
          ]} />
        </Section>

        <Section title="What we do with it">
          <List items={[
            "Show your past entries back to you, with mood pills and the weekly insight paragraph.",
            "Generate weekly AI reflections using OpenAI GPT-5.2 on your recent entries (transcript text only).",
            "Email you a password-reset link when you ask for one.",
            "Send the daily reminder you've opted into (email and/or push).",
            "Operate the service. That's it. We do not sell, rent, or share your data with advertisers.",
          ]} />
        </Section>

        <Section title="Who else sees it">
          <List items={[
            "OpenAI — receives your audio for transcription (Whisper) and your transcript text for mood analysis & weekly insights (GPT-5.2). OpenAI's API does NOT train on this data. Their policy: https://openai.com/policies/api-data-usage-policies.",
            "Resend — receives only the recipient email address + the email subject/body when we send you a transactional email.",
            "Push providers (Apple APNs, Google FCM, browser push endpoints) — receive a short notification payload (title + body + URL) for delivery.",
            "MongoDB Atlas — hosts our database.",
            "We share nothing else with any third party, ever.",
          ]} />
        </Section>

        <Section title="How long we keep it">
          We keep your data until you delete your account. Daily reminder logs are auto-purged after
          90 days. Password reset tokens expire after 1 hour and are deleted automatically.
        </Section>

        <Section title="Your rights">
          You can <b>export, edit, or delete</b> any individual entry from the app. You can permanently
          delete your account — including every entry, session, push subscription, and reminder log — by
          going to <b>Settings → Delete my account</b>. The deletion is immediate and irreversible.
          You may also email us at <a href="mailto:help@apphush.online" className="text-brand underline">help@apphush.online</a> to
          exercise any GDPR / CCPA right (access, portability, rectification, deletion, objection).
        </Section>

        <Section title="Children">
          Hush is not intended for children under 13. We do not knowingly collect data from anyone
          under 13. If you believe we have, please email us and we'll delete it.
        </Section>

        <Section title="Changes">
          If we change this policy in a way that materially affects you, we'll email you at least 7
          days before the change takes effect.
        </Section>

        <Section title="Contact">
          Questions? <a href="mailto:help@apphush.online" className="text-brand underline">help@apphush.online</a>
        </Section>
      </main>
    </div>
  );
}

function Section({ title, children }) {
  return (
    <section className="mb-10">
      <h2 className="font-serif text-2xl text-ink-900 mb-3">{title}</h2>
      <div className="text-ink-700 leading-relaxed text-[15px]">{children}</div>
    </section>
  );
}

function List({ items }) {
  return (
    <ul className="space-y-2 list-disc pl-6">
      {items.map((it, i) => (
        <li key={`${i}-${(it || "").slice(0, 24)}`}>{it}</li>
      ))}
    </ul>
  );
}
